// =============================================================================
// reportsCore.js - pure helpers for the Reports tab (no React, no network;
// unit-tested in src/lib/__tests__/reports.test.mjs). Server side lives in
// migration 20261024100000_reports_upgrade.sql: every number on the tab comes
// from a SECURITY DEFINER RPC there, so these helpers only format, rate and
// shape what the server returns.
// =============================================================================

export const DEFAULT_TZ = "America/New_York";

// ---- roles -----------------------------------------------------------------
// Mirrors public.reports_role_can() in the migration. The server is the
// authority; this decides which tabs to show.
const REPORT_ROLES = ["Owner", "Manager", "Billing", "Supervising Care Manager", "Care Manager Supervisor"];
const FINANCE_ROLES = ["Owner", "Manager", "Billing"];
const CLINICAL_LEADS = ["Owner", "Manager", "Supervising Care Manager", "Care Manager Supervisor"];
const PRACTICE_LEADS = ["Owner", "Manager"];
// Reports > Rides (ride_insights): the people who book rides.
// Billing opens it for the mileage to pay only (Rides block 8).
const RIDE_ROLES = ["Owner", "Manager", "Front Desk", "Billing"];

export function canSee(role, section) {
  switch (section) {
    case "summary": case "trends": case "compliance": case "noshow": case "gaps":
    case "screeners": case "amh": case "email":
      return REPORT_ROLES.includes(role);
    case "copay": case "financial": case "reconciliation":
      return FINANCE_ROLES.includes(role);
    case "gaps_detail": case "team":
      return CLINICAL_LEADS.includes(role);
    case "scheduled": case "feedback": case "worklist":
      return PRACTICE_LEADS.includes(role);
    case "rides":
      return RIDE_ROLES.includes(role);
    default:
      return false;
  }
}

export const REPORT_TABS = [
  ["summary", "Summary"],
  ["trends", "Trends"],
  ["compliance", "Compliance"],
  ["amh", "AMH / TCM"],
  ["team", "Care team"],
  ["noshow", "No-Shows"],
  ["rides", "Rides"],
  ["gaps", "Follow-Up Gaps"],
  ["copay", "Copay"],
  ["reconciliation", "Reconciliation"],
  ["screeners", "Screeners"],
  ["email", "Email"],
  ["feedback", "Patient Feedback"],
  ["worklist", "Worklist"],
  ["scheduled", "Scheduled"],
];

export function tabsForRole(role) {
  return REPORT_TABS.filter(([key]) => canSee(role, key));
}

// ?tab= deep link, or the first tab the role can see.
export function initialTab(want, role) {
  const tabs = tabsForRole(role);
  if (want && tabs.some(([k]) => k === want)) return want;
  return tabs.length ? tabs[0][0] : "summary";
}

// Tabs where the date-range picker means something.
export const RANGED_TABS = new Set(["summary", "compliance", "noshow", "copay", "screeners", "email", "feedback", "amh", "team", "rides"]);

export const RANGE_OPTIONS = [
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 180, label: "Last 180 days" },
  { value: 365, label: "Last 365 days" },
];

// ---- numbers ---------------------------------------------------------------
export function rate(n, d) {
  const num = Number(n);
  const den = Number(d);
  if (!den || Number.isNaN(num) || Number.isNaN(den)) return null;
  return num / den;
}
export function fmtPct(n, d) {
  const r = rate(n, d);
  return r == null ? "-" : Math.round(r * 100) + "%";
}
export function fmtRate(r) {
  return r == null || Number.isNaN(Number(r)) ? "-" : Math.round(Number(r) * 100) + "%";
}
export function fmtMoney(n) {
  const v = Number(n || 0);
  return "$" + Math.round(v).toLocaleString("en-US");
}
// Higher is better: >= good green, >= warn amber, else red. null -> neutral.
export function rateVariant(r, good = 0.7, warn = 0.4) {
  if (r == null) return "neutral";
  return r >= good ? "green" : r >= warn ? "amber" : "red";
}
// No-show: lower is better.
export function noShowVariant(r) {
  if (r == null) return "neutral";
  return r > 0.15 ? "red" : r > 0.10 ? "amber" : "green";
}

// ---- dates -----------------------------------------------------------------
export function localYmd(d = new Date(), tz = DEFAULT_TZ) {
  const parts = {};
  new Intl.DateTimeFormat("en-US", { timeZone: tz || DEFAULT_TZ, year: "numeric", month: "2-digit", day: "2-digit" })
    .formatToParts(d instanceof Date ? d : new Date(d)).forEach((p) => { parts[p.type] = p.value; });
  return parts.year + "-" + parts.month + "-" + parts.day;
}
// "2026-10-04" -> "Oct 4, 2026" without a time-zone shift.
export function fmtYmd(ymd, withYear = true) {
  if (!ymd) return "-";
  const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "-";
  return new Date(y, m - 1, d).toLocaleDateString("en-US", withYear ? { month: "short", day: "numeric", year: "numeric" } : { month: "short", day: "numeric" });
}
export function monthLabel(ymd) {
  if (!ymd) return "-";
  const [y, m] = String(ymd).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}
export function periodLabel(w) {
  if (!w || !w.from || !w.to) return "";
  return fmtYmd(w.from) + " - " + fmtYmd(w.to);
}

// ---- CSV -------------------------------------------------------------------
// File names use the practice-local day the server returned (not UTC).
export function csvFileName(prefix, days, today, tz) {
  const day = today || localYmd(new Date(), tz);
  return prefix + (days ? "_" + days + "d" : "") + "_" + day + ".csv";
}
// Formula-safe: a cell starting with = + - @ tab or CR would run as a formula
// in Excel / Sheets, so it gets a leading apostrophe and is quoted.
export function csvEscape(v) {
  if (v == null) return "";
  let s = String(v);
  const formula = /^[=+\-@\t\r]/.test(s);
  if (formula) s = "'" + s;
  return formula || /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}
export function buildCsv(rows) {
  if (!rows || rows.length === 0) return "";
  const headers = Object.keys(rows[0]);
  return [headers.join(","), ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(","))].join("\n");
}

// Follow-up gap list. days_since_last_measure is what it says; the old file
// called it days_overdue, which overstated every row by the window length.
export function gapCsvRows(rows) {
  return (rows || []).map((g) => ({
    panel: g.panel,
    measure: g.measure,
    mrn: g.mrn || "",
    patient: g.name || "",
    last_value: g.last_value ?? "",
    last_measured: g.never_measured ? "Never measured" : (g.last_measured || ""),
    days_since_last_measure: g.never_measured ? "" : (g.days_since_last ?? ""),
    days_past_window: g.never_measured ? "" : (g.days_past_window ?? ""),
    window_days: g.window_days,
    never_measured: g.never_measured ? "Yes" : "No",
  }));
}
// Counts-only version (Billing, and anyone exporting the summary).
export function gapCountCsvRows(panels) {
  return (panels || []).map((p) => ({
    panel: p.panel, measure: p.measure, window_days: p.window_days,
    eligible_patients: p.eligible, past_window: p.overdue, never_measured: p.never_measured,
  }));
}
export function gapBadge(g) {
  if (g.never_measured || g.days_since_last == null) return { label: "Never", variant: "red" };
  const over = g.days_past_window != null ? g.days_past_window : g.days_since_last - (g.window_days || 0);
  return { label: over + "d past", variant: g.days_since_last > (g.window_days || 0) * 2 ? "red" : "amber" };
}

// ---- no-shows --------------------------------------------------------------
export const DOW_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export function withRates(rows) {
  return (rows || []).map((r) => ({ ...r, rate: rate(r.no_show, r.total) }));
}
export function noShowCsvRows(byProvider, byDow) {
  return withRates(byProvider).map((p) => ({ dimension: "Provider", name: p.name, total: p.total, no_shows: p.no_show, no_show_pct: p.rate == null ? "" : Math.round(p.rate * 100) }))
    .concat(withRates(byDow).map((d) => ({ dimension: "Day of week", name: DOW_LABELS[d.dow], total: d.total, no_shows: d.no_show, no_show_pct: d.rate == null ? "" : Math.round(d.rate * 100) })));
}

// ---- copay -----------------------------------------------------------------
export function copayTrend(c) {
  if (!c) return { rate: null, first: null, second: null, delta: null };
  const first = rate(c.h1_collected, c.h1_expected);
  const second = rate(c.h2_collected, c.h2_expected);
  return { rate: rate(c.collected, c.expected), first, second, delta: first == null || second == null ? null : second - first };
}

// ---- AMH / TCM -------------------------------------------------------------
// Direction-aware comparison with the NC reference target. Rates are percents.
export function measureStatus(m) {
  if (!m || m.rate == null) return { status: "no_data", label: "No data", variant: "neutral", gap: null };
  if (m.target_rate == null) return { status: "no_target", label: "No NC target", variant: "neutral", gap: null };
  const r = Number(m.rate);
  const t = Number(m.target_rate);
  const lower = m.direction === "lower_is_better";
  const meets = lower ? r <= t : r >= t;
  const gap = Math.round((lower ? t - r : r - t) * 10) / 10;
  return meets
    ? { status: "meets", label: "Meets target", variant: "green", gap }
    : { status: "below", label: "Below target", variant: "red", gap };
}
export function measureName(m) {
  return m.measure_code + (m.submeasure ? " (" + m.submeasure + ")" : "");
}
export function rateDelta(m) {
  if (!m || m.rate == null || m.prev_rate == null) return null;
  return Math.round((Number(m.rate) - Number(m.prev_rate)) * 10) / 10;
}
export function tcmTotals(rows) {
  const t = { episodes: 0, open: 0, closed: 0, outreach_on_time: 0, outreach_late: 0, outreach_missing: 0, med_rec_required: 0, med_rec_on_time: 0, med_rec_outstanding: 0, all_elements_complete: 0 };
  for (const r of rows || []) for (const k of Object.keys(t)) t[k] += Number(r[k] || 0);
  return {
    ...t,
    outreachOnTimeRate: rate(t.outreach_on_time, t.episodes),
    medRecOnTimeRate: rate(t.med_rec_on_time, t.med_rec_required),
    completionRate: rate(t.all_elements_complete, t.episodes),
  };
}
export function contactTotals(rows) {
  const t = { active: 0, contacted: 0, in_person: 0, successful_contacts: 0 };
  for (const r of rows || []) for (const k of Object.keys(t)) t[k] += Number(r[k] || 0);
  return { ...t, contactRate: rate(t.contacted, t.active), inPersonRate: rate(t.in_person, t.active) };
}
export function prlBacklogRows(prl) {
  return Number((prl && prl.unmatched) || 0) + Number((prl && prl.multi_match) || 0) + Number((prl && prl.errors) || 0);
}

// ---- care team -------------------------------------------------------------
// Successful contacts per caseload member per 30-day month.
export function perMemberMonth(contacts, caseload, days) {
  const c = Number(contacts || 0);
  const n = Number(caseload || 0);
  const d = Number(days || 0);
  if (!n || !d) return null;
  return Math.round((c / n / (d / 30)) * 100) / 100;
}
export const CHW_METRICS = [
  { key: "engagements_week", label: "Engagements / week" },
  { key: "successful_contacts_week", label: "Successful contacts / week" },
  { key: "members_touched_month", label: "Members touched / month" },
  { key: "utr_members_engaged_month", label: "UTR engaged / month" },
];
export function chwActual(row, key, days) {
  const weeks = Number(days || 0) / 7;
  switch (key) {
    case "engagements_week": return weeks ? Math.round((Number(row.engagements || 0) / weeks) * 10) / 10 : null;
    case "successful_contacts_week": return weeks ? Math.round((Number(row.successful_contacts || 0) / weeks) * 10) / 10 : null;
    case "members_touched_month": return Number(row.members_touched_30d || 0);
    default: return null; // not tracked in this report
  }
}
export function chwVsTargets(row, days) {
  const targets = (row && row.targets) || {};
  return CHW_METRICS.map((m) => {
    const actual = chwActual(row || {}, m.key, days);
    const target = targets[m.key] != null ? Number(targets[m.key]) : null;
    const r = actual == null || !target ? null : actual / target;
    return { ...m, actual, target, rate: r, variant: r == null ? "neutral" : rateVariant(r, 1, 0.75) };
  });
}

// ---- trends ----------------------------------------------------------------
export const TREND_METRICS = [
  { key: "visits_completed", label: "Visits completed", unit: "" },
  { key: "no_show_rate", label: "No-show rate", unit: "%", lowerBetter: true },
  { key: "copay_rate", label: "Copays collected", unit: "%" },
  { key: "cm_successful_contacts", label: "CM successful contacts", unit: "" },
  { key: "screeners_completed", label: "Screeners completed", unit: "" },
  { key: "tc_outreach_rate", label: "Transitional care outreach on time", unit: "%" },
];
// Copay dollars are for Owner / Manager / Billing only (the server strips
// them for other roles too).
export function trendMetricsForRole(role) {
  return TREND_METRICS.filter((t) => t.key !== "copay_rate" || canSee(role, "copay"));
}
export function trendValue(metrics, key) {
  const m = metrics || {};
  const pctOf = (n, d) => { const r = rate(n, d); return r == null ? null : Math.round(r * 1000) / 10; };
  switch (key) {
    case "no_show_rate": return pctOf(m.no_shows, m.visits_scheduled);
    case "copay_rate": return pctOf(m.copay_collected, m.copay_expected);
    case "tc_outreach_rate": return pctOf(m.tc_outreach_on_time, m.tc_episodes);
    default: return m[key] == null ? null : Number(m[key]);
  }
}
export function trendSeries(months, key) {
  return (months || [])
    .map((mo) => ({ date: mo.month, value: trendValue(mo.metrics, key), partial: mo.source === "live_partial" }))
    .filter((p) => p.value != null && !Number.isNaN(p.value));
}
export function fmtMetric(v, unit) {
  if (v == null || Number.isNaN(Number(v))) return "-";
  const n = Number(v);
  const s = Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, "");
  return unit === "%" ? s + "%" : s;
}
// Last complete month vs the one before (the current month is partial).
export function monthOverMonth(series) {
  const done = (series || []).filter((p) => !p.partial);
  if (done.length < 2) return null;
  const a = done[done.length - 2];
  const b = done[done.length - 1];
  return { from: a, to: b, delta: Math.round((b.value - a.value) * 10) / 10 };
}

// ---- scheduled reports -----------------------------------------------------
export const DEFINITION_REPORTS = [
  { value: "summary", label: "Practice summary" },
  { value: "trends", label: "Month over month" },
  { value: "amh", label: "AMH / TCM compliance" },
  { value: "team", label: "Care team productivity" },
  { value: "noshow", label: "No-shows" },
  { value: "gaps", label: "Follow-up gaps (counts)" },
  { value: "copay", label: "Copay collection" },
  { value: "screeners", label: "Screeners" },
];
export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export function definitionLabel(key) {
  const d = DEFINITION_REPORTS.find((x) => x.value === key);
  return d ? d.label : key;
}
// Recipient roles allowed for a report (same rule as the server).
export function recipientAllowed(role, reportKey) {
  return canSee(role, reportKey);
}
export function validateDefinition(f) {
  const name = String((f && f.name) || "").trim();
  if (!name) return "Give the report a name.";
  if (name.length > 80) return "Keep the name under 80 characters.";
  if (!DEFINITION_REPORTS.some((d) => d.value === f.report_key)) return "Pick a report.";
  if (![7, 30, 90, 180, 365].includes(Number(f.range_days))) return "Pick a date range.";
  if (f.delivery === "weekly_email") {
    const ids = f.recipient_user_ids || [];
    if (ids.length === 0) return "Add at least one recipient for the weekly email.";
    if (ids.length > 20) return "Up to 20 recipients.";
  }
  return null;
}

// ---- errors ----------------------------------------------------------------
const ERRORS = {
  not_authorized: "Your role cannot see this report.",
  panel_not_found: "That clinical panel no longer exists.",
  definition_not_found: "That saved report no longer exists.",
  definition_archived: "That saved report was removed.",
  name_required: "Give the report a name.",
  invalid_report: "Pick a report.",
  invalid_range: "Pick a date range.",
  invalid_delivery: "Pick how it is delivered.",
  invalid_weekday: "Pick a day of the week.",
  too_many_recipients: "Up to 20 recipients.",
  recipients_required: "Add at least one recipient.",
  recipient_not_eligible: "One of the recipients cannot receive this report (role or missing email).",
  sent_recently: "This report was sent in the last 10 minutes. Try again later.",
};
export function reportsErrorText(e) {
  const msg = String((e && (e.message || e.error_description)) || e || "");
  for (const [code, text] of Object.entries(ERRORS)) if (msg.includes(code)) return text;
  if (/Failed to fetch|NetworkError|network/i.test(msg)) return "Could not reach the server. Check the connection and try again.";
  return msg || "Something went wrong loading this report.";
}
